/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/require-computed-property-dependencies, getter-return, no-prototype-builtins, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { isArray } from '@ember/array';
import { get, set, action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import NotificationType from 'linced-app/enums/notification-type';
import { filterValue } from '@linc-technologies/linced-common/utils/filtering';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import SchoolStatus from '@linc-technologies/linced-common/enums/school-status';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import moment from 'moment';
import { observes } from '@ember-decorators/object';
import {
  byKeys,
  filterBy,
  findBy,
  mapBy,
  without,
} from '@linc-technologies/linced-common/utils/arrays';

export default class AdminController extends Controller {
  // Services
  @service
  attendanceCodes;

  @service
  attendanceMessages;

  @service
  dates;

  @service
  options;

  @service
  paperToaster;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  strings;

  SchoolStatus = SchoolStatus;
  Jurisdiction = Jurisdiction;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('jurisdiction')
  get trackMovements() {
    return this.jurisdiction !== Jurisdiction.NZ_MOE;
  }

  @computed
  get thisYear() {
    return moment().format('YYYY');
  }

  @computed('session.activeSchool', 'thisYear')
  get classGroups() {
    let schoolId = this.get('session.activeSchool');
    return this.store
      .peekAll('group')
      .filter((group) => {
        let inThisSchool = group.get('schoolId.id') === schoolId;
        let isClassOrHomeroom =
          group.type === GroupType.CLASS || group.type === GroupType.HOMEROOM;
        let isThisYear = group.year === parseInt(this.thisYear, 10);
        return inThisSchool && isClassOrHomeroom && isThisYear;
      })
      .sort(byKeys('name'));
  }

  @alias('model.date')
  dateObj;

  @alias('dateObj.date')
  date;

  @computed('date')
  get dateFormatted() {
    return moment(this.date, 'YYYY-MM-DD').format('D MMM YYYY');
  }

  @computed('dates.today.id', 'dateObj.id')
  get isToday() {
    return this.dateObj.id === this.dates.today.id;
  }

  @computed('date', 'dates.structures.[]')
  get day() {
    return this.dates.dayForDate(this.date);
  }

  @computed('day.periods.length')
  get hasMultiplePeriods() {
    let numPeriods = this.get('day.periods.length');
    return !isEmpty(numPeriods) && numPeriods > 1;
  }

  @computed('hasMultiplePeriods', 'period', 'day.periods.[]')
  get hasAllDay() {
    return (
      this.hasMultiplePeriods && this.day.periods.indexOf(this.period) === 0
    );
  }

  @tracked period = null;
  hasRegisters = false;
  hasRecords = false;
  @tracked
  attendanceGroup = null;

  @computed('model.records.@each.id', 'period.id')
  get records() {
    // The extra filter on person.id is to ensure that we only look at valid records with a PersonId.
    return this.get('model.records').filter(
      (item) => item.period_id === this.get('period.id') && item.person_id,
    );
  }

  @computed('model.registers.@each.id', 'period.id')
  get registers() {
    return this.get('model.registers').filter(
      (item) => item.period.id === this.get('period.id'),
    );
  }

  @computed('records.@each.code')
  get present() {
    let presentCodes = mapBy(
      filterBy(this.get('attendanceCodes.allCodes'), 'inClass'),
      'code',
    );
    let records = this.records;

    if (isArray(records)) {
      return records.filter((record) => {
        return presentCodes.includes(record.get('code'));
      });
    } else {
      return [];
    }
  }

  @alias('present.length')
  numPresent;

  @computed('records.@each.code')
  get unexplained() {
    let unknownCode = this.get('attendanceCodes.unknown.code');
    let records = this.records;

    if (isArray(records)) {
      let unexplained = filterBy(records, 'code', unknownCode);

      if (unexplained.length) {
        // Also add any messages from caregivers
        let personIds = unexplained.map((record) => {
          return record.get('person.id');
        });
        this.attendanceMessages
          .queryDay(personIds, this.date)
          .then((messages) => {
            unexplained.forEach((record) => {
              record.set(
                'messages',
                filterBy(messages, 'person.id', record.get('person.id')),
              );
            });
          });
      }

      return unexplained;
    } else {
      return [];
    }
  }

  @alias('unexplained.length')
  numUnexplained;

  // unexplainedFilters specifies filters for the Unexplained list.
  @tracked
  unexplainedFilters = [];

  @computed('unexplained.[]', 'unexplainedFilters.@each.{field,value}')
  get filteredUnexplained() {
    if (isEmpty(this.unexplainedFilters)) {
      return this.unexplained;
    }
    return this.unexplained.filter((row) => {
      return this.unexplainedFilters.every((filter) => {
        let cellValue = get(row.person, filter.field) ?? '';
        return filterValue(cellValue, DataType.STRING, filter.value);
      });
    });
  }

  @computed('records.@each.code', 'trackMovements', 'attendanceCodes.tallyMap')
  get tallies() {
    let tallyMap = this.attendanceCodes.tallyMap;
    if (this.trackMovements) {
      // TODO: Replace this with a Movement-based approach.
      return this.records.reduce(
        (tallies, record) => {
          if (record.movements.length <= 1) {
            // Only one movement - tally it as full-day.
            if (tallyMap.hasOwnProperty(record.code)) {
              let tallyLabel = tallyMap[record.code];
              if (!tallies.hasOwnProperty(tallyLabel)) {
                tallies[tallyLabel] = { full: 0, partial: 0 };
              }
              tallies[tallyLabel].full++;
              tallies['total'].full++;
            }
          } else {
            // Multiple movements - tally each as a partial absence.
            return record.movements.reduce((tallies, movement) => {
              if (tallyMap.hasOwnProperty(movement.code)) {
                let tallyLabel = tallyMap[movement.code];
                if (!tallies.hasOwnProperty(tallyLabel)) {
                  tallies[tallyLabel] = { full: 0, partial: 0 };
                }
                tallies[tallyLabel].partial++;
              }
              return tallies;
            }, tallies);
          }
          return tallies;
        },
        { total: { full: 0, partial: 0 } },
      );
    } else {
      return this.records.reduce(
        (tallies, record) => {
          if (tallyMap.hasOwnProperty(record.code)) {
            let tallyLabel = tallyMap[record.code];
            if (!tallies.hasOwnProperty(tallyLabel)) {
              tallies[tallyLabel] = { full: 0, partial: 0 };
            }
            tallies[tallyLabel].full++;
            tallies['total'].full++;
          }
          return tallies;
        },
        { total: { full: 0, partial: 0 } },
      );
    }
  }

  @computed('tallies')
  get tallyLabels() {
    return without(Object.keys(this.tallies), 'total');
  }

  @observes(
    'day',
    'day.periods.[]',
    'date',
    'dates.currentTimetableDay.periods.[]',
  )
  _setInitialPeriod() {
    // The open attendance dialog keeps its period. A new period resets the
    // dialog, and the unsaved marks are lost.
    if (!this.takingAttendance) {
      this.resetPeriod();
    }
  }

  @optionAlias('notifications:sms:enable')
  smsEnabled;
  @optionAlias('notifications:sms:enableAdminAbsenceSend')
  absenceSmsEnabled;
  @optionAlias('notifications:sms:absenceMessage')
  absenceSmsTemplate;
  @tracked
  toNotify = [];

  @observes('records.@each.id')
  _resetNotifySelection() {
    this.toNotify = [];
  }

  @tracked
  absenceSmsSendInProgress = false;

  @computed('model.alertLogs.[]')
  get sentAbsenceSmsMap() {
    return this.model.alertLogs.reduce((mapped, alertLog) => {
      alertLog.personIds.forEach((id) => {
        if (isEmpty(mapped[id])) {
          mapped[id] = [];
        }
        mapped[id].push(alertLog);
      });
      return mapped;
    }, {});
  }

  @alias('session.school.absenceReportingAvailable')
  absenceReportingAvailable;

  init() {
    super.init(...arguments);
    this.resetPeriod();
  }

  @computed('day', 'period')
  get previousPeriods() {
    let day = this.day;
    if (!isEmpty(day)) {
      let periods = day.get('periods');
      if (!isEmpty(periods)) {
        periods = [...periods];
        let periodIndex = periods.findIndex((periodRecord) => {
          return periodRecord.id === this.period.id;
        });
        if (periodIndex < 5) {
          return periods.slice(0, periodIndex);
        } else {
          return periods.slice(periodIndex - 5, periodIndex);
        }
      }
    }
  }

  // Conflict dialogs - hoisted from attendance-unexplained component as it's
  // removed from the DOM once the record's updated (if no longer unexplained).
  @tracked
  conflictRecord = null;

  @tracked
  conflictPreviousCode = '';
  @tracked
  conflictAttemptedCode = '';
  @tracked
  conflictDialogOpen = false;
  @tracked
  noteConflictDialogOpen = false;
  @tracked
  conflictAttemptedNotes = '';

  @tracked
  noteConflictSaving;

  @tracked
  takingAttendance;

  @action
  changeDate(date) {
    Promise.resolve(this.dates.getDate(date))
      .then((dateObj) => {
        if (isEmpty(dateObj)) {
          // We don't have a date object loaded for this date.
          // Load the year containing the date, then try again.
          let year = moment(this.dates.previousRolloverDate(date)).year();
          return this.dates.loadYear(year).then(() => {
            return this.dates.getDate(date);
          });
        }
        return dateObj;
      })
      .then((dateObj) => {
        if (dateObj) {
          this.router.transitionTo('/attendance/' + dateObj.get('id'));
        }
      });
  }

  @action
  selectPeriod(period) {
    this.period = period;
  }

  @action
  markAttendance(group) {
    this.attendanceGroup = group;
    this.takingAttendance = true;
  }

  @action
  closeAttendance(saved) {
    if (saved) {
      this.model.registers.update();
      this.model.records.update();
      if (this.jurisdiction !== Jurisdiction.NZ_MOE) {
        this.model.auditLogs.update();
      }
    }

    this.takingAttendance = false;
  }

  @action
  refreshRecords() {
    this.model.records.update();
    if (this.jurisdiction !== Jurisdiction.NZ_MOE) {
      this.model.auditLogs.update();
    }
  }

  @action
  filterUnexplained(field, value) {
    let filter = findBy(this.unexplainedFilters, 'field', field);
    if (isEmpty(filter)) {
      // No existing filter on this field; add it if applicable.
      if (!isEmpty(value)) {
        this.unexplainedFilters = [
          ...this.unexplainedFilters,
          { field, value },
        ];
      }
      return;
    }

    if (isEmpty(value)) {
      // Filter has been cleared; remove it.
      this.unexplainedFilters = this.unexplainedFilters.filter(
        (f) => f !== filter,
      );
    } else {
      // Existing filter has been changed; update it.
      set(filter, 'value', value);
    }
  }

  @action
  toggleSms(record) {
    if (this.toNotify.includes(record)) {
      this.toNotify = this.toNotify.filter((r) => r !== record);
    } else {
      this.toNotify = [...this.toNotify, record];
    }
  }

  @action
  selectAllUnsent() {
    let sent = Object.keys(this.sentAbsenceSmsMap);
    let toNotify = [];
    this.unexplained.forEach((record) => {
      if (!sent.includes(record.get('person.id'))) {
        let canSend = record
          .get('person.learner.primaryContacts')
          .some((contact) => {
            return contact.receivesSms && !isEmpty(contact.phoneMobile);
          });
        if (canSend) {
          toNotify.push(record);
        }
      }
    });
    this.toNotify = toNotify;
  }

  @action
  deselectAllSms() {
    this.toNotify = [];
  }

  @action
  sendAbsenceSms() {
    this.absenceSmsSendInProgress = true;

    let recipients = [];
    let template = this.absenceSmsTemplate.trim();
    if (isEmpty(template)) {
      template =
        '{CHILD} has been marked as absent from school today.  Please reply to let us know why {CHILD} is not here.';
    }
    this.toNotify.forEach((record) => {
      // Skip pending contacts, as we don't have their personId and so can't build a valid recipient.
      let smsContacts = filterBy(
        record.get('person.learner.primaryContacts'),
        'receivesSms',
      );
      let learnerName = record.get('person.firstName');
      let personalisedMessage = template.replace(/{CHILD}/g, learnerName);
      recipients.push(
        ...smsContacts.map((contact) => {
          return {
            personId: contact.caregiverId,
            personalisedMessage,
          };
        }),
      );
    });
    let notification = this.store.createRecord('notifications/notification', {
      schoolId: this.session.school,
      notificationType: NotificationType.ABSENCE_ALERT,
      params: {
        recipients,
      },
    });
    notification
      .save()
      .then((notification) => {
        let alertLog = this.store.createRecord('attendance/alert-log', {
          schoolId: this.session.school,
          notificationId: notification,
          dateId: this.dateObj,
          date: this.date,
          authorId: this.session.person,
          personIds: mapBy(this.toNotify, 'person.id'),
        });

        return alertLog
          .save()
          .then(() => {
            this.paperToaster.show(
              'Absence SMS message(s) queued successfully.',
              {
                duration: 4000,
                position: 'top right',
                toastClass: 'success',
              },
            );
          })
          .catch(() => {
            this.paperToaster.show(
              'The requested messages were queued, but an error occurred while saving details of absence SMS message(s).',
              {
                duration: 6000,
                position: 'top right',
                toastClass: 'warning',
              },
            );
          });
      })
      .catch(() => {
        this.paperToaster.show('Error sending absence SMS message(s).', {
          duration: 4000,
          position: 'top right',
          toastClass: 'warning',
        });
      })
      .finally(() => {
        this.absenceSmsSendInProgress = false;
        this.toNotify = [];
        this.model.alertLogs.update();
      });
  }

  @action
  openConflictDialog(record, previousCode, attemptedCode) {
    this.conflictRecord = record;
    this.conflictPreviousCode = previousCode;
    this.conflictAttemptedCode = attemptedCode;
    this.conflictDialogOpen = true;
  }

  @action
  closeConflictDialog() {
    this.conflictDialogOpen = false;
  }

  @action
  openNoteConflictDialog(record, attemptedNotes) {
    this.conflictRecord = record;
    this.conflictAttemptedNotes = attemptedNotes;
    this.noteConflictDialogOpen = true;
  }

  @action
  closeNoteConflictDialog() {
    this.noteConflictDialogOpen = false;
  }

  @action
  saveConflictingNote() {
    this.noteConflictSaving = true;
    let effectiveFrom;
    if (this.trackMovements) {
      effectiveFrom = this.conflictRecord.get('movements.lastObject.time');
      let movement = findBy(
        this.get('conflictRecord.movements'),
        'time',
        effectiveFrom,
      );
      if (!isEmpty(movement)) {
        movement.set('notes', this.conflictAttemptedNotes);
      }
    }
    this.conflictRecord.setProperties({
      notes: this.conflictAttemptedNotes,
      reason: 'Added note',
      effectiveFrom,
    });
    this.conflictRecord.save().then(() => {
      this.noteConflictDialogOpen = false;
      this.noteConflictSaving = false;
      this.paperToaster.show(`Attendance data saved.`, {
        duration: 4000,
        position: 'top right',
        toastClass: 'success',
      });
    });
  }

  // timetableDay is the day of the page date. On the first visit to today,
  // before the day loads, it is the current timetable day.
  get timetableDay() {
    return isEmpty(this.day) ? this.dates.currentTimetableDay : this.day;
  }

  // currentPeriod is the period that contains the current time (or the
  // closest past period) when the page shows today. On other dates it is
  // null. It changes each minute with dates.currentTime.
  get currentPeriod() {
    let periods = this.timetableDay?.periods;
    if (this.date !== get(this, 'dates.currentDate') || isEmpty(periods)) {
      return null;
    }

    let now = moment(this.dates.currentTime, 'HH:mm');
    let period = null;
    periods.forEach(function (p) {
      if (
        now.isSameOrAfter(moment(p.get('startTime'), 'HH:mm')) &&
        now.isBefore(moment(p.get('endTime'), 'HH:mm'))
      ) {
        period = p;
      }
    });

    if (isEmpty(period)) {
      // It's not currently within a period
      if (now.isBefore(moment(periods[0]?.startTime, 'HH:mm'))) {
        // Current time is before first period; select first period.
        period = periods[0];
      } else if (now.isAfter(moment(periods.at(-1)?.endTime, 'HH:mm'))) {
        // Current time is after last period; select last period.
        period = periods.at(-1);
      } else {
        // Between periods.  Select the previous period.
        periods.forEach(function (p) {
          if (now.isSameOrAfter(moment(p.get('endTime'), 'HH:mm'))) {
            period = p;
          }
        });
      }
    }
    return period;
  }

  resetPeriod() {
    let period = this.currentPeriod;
    if (this.date !== this.get('dates.currentDate')) {
      // Default to first period if we're not looking at today.
      period = this.timetableDay?.periods?.[0] ?? null;
    }
    // Assign only a new period. The same period again resets the open
    // attendance dialog.
    if (this.period !== period) {
      this.period = period;
    }
  }

  // resetFilters removes all filters.
  resetFilters() {
    this.unexplainedFilters = [];
  }
}
